<template>
  <div class="noticia-markdown-editor">
    <MdEditor
      ref="editor"
      :model-value="modelValue"
      :toolbars="toolbars"
      :max-length="maxLength"
      language="es-ES"
      :show-toolbar-name="true"
      :preview="true"
      :html-preview="false"
      :no-prettier="true"
      :no-upload-img="false"
      :no-mermaid="true"
      :no-katex="true"
      :no-echarts="true"
      :no-highlight="true"
      :sanitize="sanitizeEditorHtml"
      :footers="['markdownTotal']"
      :on-drop="handleDrop"
      :on-upload-img="onUploadImg"
      :key="editorRenderKey"
      @update:model-value="$emit('update:modelValue', $event)"
    />
  </div>
</template>

<script>
import { MdEditor, config } from 'md-editor-v3'
import { EditorView } from '@codemirror/view'
import Cropper from 'cropperjs'
import 'md-editor-v3/lib/style.css'
import 'cropperjs/dist/cropper.min.css'
import {
  getNoticiaImageReferences,
  resolveNoticiaImageSources,
  sanitizeNoticiaHtml,
  MAX_NOTICIA_EMBEDDED_IMAGE_SIZE_BYTES
} from './noticiaMarkdown'
import { showError } from '../Mensaje'

const readCspNonce = () => {
  const metaNonce = document.querySelector('meta[name="csp-nonce"]')
    || document.querySelector('meta[property="csp-nonce"]')

  if (metaNonce && metaNonce.content) {
    return metaNonce.content
  }

  const scriptWithNonce = document.querySelector('script[nonce]')
  if (scriptWithNonce) {
    return scriptWithNonce.getAttribute('nonce') || ''
  }

  return window.__CSP_NONCE__ || ''
}

const cspNonce = readCspNonce()

config({
  editorExtensions: {
    cropper: {
      instance: Cropper
    }
  },
  codeMirrorExtensions: (extensions) => {
    if (!cspNonce) {
      return extensions
    }

    return [
      ...extensions,
      {
        type: 'cspNonce',
        extension: EditorView.cspNonce.of(cspNonce)
      }
    ]
  },
  editorConfig: {
    languageUserDefined: {
      'es-ES': {
        toolbarTips: {
          bold: 'Negrita',
          underline: 'Subrayado',
          italic: 'Cursiva',
          title: 'Encabezado',
          quote: 'Cita',
          unorderedList: 'Lista sin orden',
          orderedList: 'Lista ordenada',
          link: 'Enlace',
          revoke: 'Deshacer',
          next: 'Rehacer',
          previewOnly: 'Solo vista previa'
        },
        titleItem: {
          h2: 'Encabezado 2',
          h3: 'Encabezado 3',
          h4: 'Encabezado 4'
        },
        linkModalTips: {
          linkTitle: 'Agregar enlace',
          descLabel: 'Texto del enlace:',
          descLabelPlaceHolder: 'Ingrese el texto del enlace...',
          urlLabel: 'Dirección del enlace:',
          urlLabelPlaceHolder: 'Ingrese una URL http:// o https://...',
          buttonOK: 'Aceptar'
        }
      }
    }
  }
})

export default {
  name: 'EditorMarkdownNoticias',
  components: { MdEditor },
  props: {
    modelValue: {
      type: String,
      default: ''
    },
    maxLength: {
      type: Number,
      default: 4000
    },
    idCarpetaTemporal: {
      type: String,
      default: ''
    }
  },
  emits: ['update:modelValue', 'file-blocked', 'image-uploaded'],
  data() {
    return {
      temporaryImagePreviewUrls: new Map(),
      protectedImagePreviewUrls: new Map(),
      protectedImageObjectUrls: [],
      protectedImageReferencesKey: '',
      editorRenderKey: 0,
      toolbars: [
        'bold',
        'underline',
        'italic',
        'title',
        'quote',
        'link',
        'image',
        'unorderedList',
        'orderedList',
        '=',
        'revoke',
        'next',
        'previewOnly'
      ]
    }
  },
  beforeUnmount() {
    this.temporaryImagePreviewUrls.forEach(url => URL.revokeObjectURL(url))
    this.temporaryImagePreviewUrls.clear()
    this.releaseProtectedImagePreviews()
  },
  watch: {
    modelValue: {
      immediate: true,
      handler(value) {
        this.refreshProtectedImagePreviews(value)
      }
    }
  },
  methods: {
    async refreshProtectedImagePreviews(markdown) {
      const references = getNoticiaImageReferences(markdown)
      const referencesKey = references.join('\n')
      if (referencesKey === this.protectedImageReferencesKey) {
        return
      }

      this.protectedImageReferencesKey = referencesKey
      this.releaseProtectedImagePreviews()

      if (references.length === 0) {
        return
      }

      const resolved = await resolveNoticiaImageSources(markdown)
      if (referencesKey !== this.protectedImageReferencesKey) {
        resolved.objectUrls.forEach(url => URL.revokeObjectURL(url))
        return
      }

      this.protectedImageObjectUrls = resolved.objectUrls
      this.protectedImagePreviewUrls = new Map(Object.entries(resolved.sourceMap))
      this.editorRenderKey += 1
    },

    releaseProtectedImagePreviews() {
      this.protectedImageObjectUrls.forEach(url => URL.revokeObjectURL(url))
      this.protectedImageObjectUrls = []
      this.protectedImagePreviewUrls.clear()
    },

    sanitizeEditorHtml(html) {
      const parsedHtml = new DOMParser().parseFromString(html, 'text/html')

      parsedHtml.querySelectorAll('img[src]').forEach(image => {
        const source = image.getAttribute('src')
        const previewUrl = this.temporaryImagePreviewUrls.get(source)
          || this.protectedImagePreviewUrls.get(source)

        if (previewUrl) {
          image.setAttribute('src', previewUrl)
        }
      })

      return sanitizeNoticiaHtml(parsedHtml.body.innerHTML, { allowLocalBlob: true })
    },
    async onUploadImg(files, callback) {
      if (!this.idCarpetaTemporal) {
        showError(this.$t('NOTICIAS_cuerpo_image_upload_error'))
        return
      }

      const referencias = []

      for (const file of files) {
        if (file && file.size > MAX_NOTICIA_EMBEDDED_IMAGE_SIZE_BYTES) {
          showError(this.$t('NOTICIAS_cuerpo_image_too_large'))
          return
        }

        try {
          const resultado = await this.$store.dispatch('gestionNoticias/uploadImagenEmbebidaTemporal', {
            file,
            idCarpetaTemporal: this.idCarpetaTemporal
          })

          if (!resultado || resultado.CodigoResultado !== 0 || !resultado.Datos || !resultado.Datos.ReferenciaTemporal) {
            const message = (resultado && resultado.DescripcionResultado)
              ? resultado.DescripcionResultado
              : this.$t('NOTICIAS_error')

            showError(message)
            return
          }

          const referenciaTemporal = resultado.Datos.ReferenciaTemporal
          this.temporaryImagePreviewUrls.set(referenciaTemporal, URL.createObjectURL(file))
          referencias.push(referenciaTemporal)
          this.$emit('image-uploaded', resultado.Datos)
        } catch (error) {
          const message = error && error.response && error.response.status === 413
            ? this.$t('NOTICIAS_cuerpo_image_too_large')
            : this.$t('NOTICIAS_cuerpo_image_upload_error')

          showError(message)
          return
        }
      }

      if (referencias.length > 0) {
        callback(referencias)
      }
    },
    handleDrop(event) {
      const files = Array.from(event?.dataTransfer?.files || [])
      if (files.length === 0) {
        return false
      }

      event.preventDefault()
      event.stopPropagation()

      if (!files.every(file => this.isImageFile(file))) {
        this.$emit('file-blocked')
        return true
      }

      this.onUploadImg(files, referencias => this.insertDroppedImages(referencias))
      return true
    },
    insertDroppedImages(referencias) {
      const editorView = this.$refs.editor?.getEditorView()
      if (!editorView || referencias.length === 0) {
        return
      }

      const imageMarkdown = referencias
        .map(referencia => `![](${referencia})`)
        .join('\n')
      const selection = editorView.state.selection.main

      editorView.dispatch({
        changes: {
          from: selection.from,
          to: selection.to,
          insert: imageMarkdown
        },
        selection: {
          anchor: selection.from + imageMarkdown.length
        }
      })
      editorView.focus()
    },
    isImageFile(file) {
      return /^image\//i.test(file.type || '')
        || /\.(png|jpe?g|webp)$/i.test(file.name || '')
    }
  }
}
</script>

<style scoped>
.noticia-markdown-editor {
  width: 100%;
}

.noticia-markdown-editor :deep(.md-editor) {
  min-height: 360px;
}

.noticia-markdown-editor :deep(.md-editor-menu-item-title:nth-child(1)),
.noticia-markdown-editor :deep(.md-editor-menu-item-title:nth-child(5)),
.noticia-markdown-editor :deep(.md-editor-menu-item-title:nth-child(6)) {
  display: none;
}

.noticia-markdown-editor :deep(.md-editor-menu-item-image:first-child) {
  display: none;
}

.noticia-markdown-editor :deep(.md-editor-preview blockquote) {
  border-left: 4px solid var(--ds-color-brand-500) !important;
  background-color: var(--ds-color-brand-50) !important;
  color: var(--ds-color-brand-900) !important;
}

.noticia-markdown-editor :deep(.md-editor-preview img) {
  display: block;
  width: 360px;
  max-width: 100%;
  height: 220px;
  margin: 16px auto;
  object-fit: contain;
}

@media (max-width: 600px) {
  .noticia-markdown-editor :deep(.md-editor) {
    min-height: 300px;
  }

  .noticia-markdown-editor :deep(.md-editor-preview img) {
    width: 100%;
    max-width: 320px;
    height: 200px;
  }
}
</style>